import type { ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  GridCells,
  GridRowEnd,
  GridHeaderRow,
  GRID_LIST_CELL_CLASS,
  GRID_LIST_ROW_CLASS,
  type GridSort,
} from "@/modules/shared/dataGrid/GridTableParts";
import type { GridColumn } from "@/modules/shared/dataGrid/gridTypes";
import {
  useDataGrid,
  useGridClientRows,
} from "@/modules/shared/dataGrid/useDataGrid";

/**
 * A whole table for rows already loaded (record tabs, panels): Filter and
 * Columns buttons, chips, header sort and the rows. Columns and filters are
 * saved per member under `tableId`.
 */
export const DataGridTable = <Row,>({
  tableId,
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  initialSort = null,
  trailingHead,
  trailingCell,
  emptyFilteredText = "Nothing matches these filters.",
  toolbarEnd,
  className,
  tableClassName,
}: {
  tableId: string;
  columns: GridColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string | number;
  onRowClick?: (row: Row) => void;
  rowClassName?: (row: Row) => string | undefined;
  initialSort?: GridSort | null;
  /** Extra header cell after the columns (e.g. Actions). */
  trailingHead?: ReactNode;
  trailingCell?: (row: Row) => ReactNode;
  emptyFilteredText?: string;
  /** Extra controls at the end of the toolbar (e.g. Add button). */
  toolbarEnd?: ReactNode;
  className?: string;
  tableClassName?: string;
}) => {
  const grid = useDataGrid({ tableId, columns });
  const {
    rows: visibleRows,
    sort,
    onSort,
  } = useGridClientRows(rows, columns, grid.filters, initialSort);

  return (
    <div className={cn("flex min-w-0 flex-col gap-2", className)}>
      <div className="flex flex-wrap items-center gap-2">
        {grid.filterButton}
        {grid.columnsButton}
        {grid.chips}
        {toolbarEnd ? <div className="ml-auto">{toolbarEnd}</div> : null}
      </div>
      {visibleRows.length === 0 && grid.filters.length ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {emptyFilteredText}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table className={tableClassName}>
            <TableHeader>
              <GridHeaderRow
                grid={grid.header}
                columns={grid.visible}
                sort={sort}
                onSort={onSort}
                trailing={trailingHead}
              />
            </TableHeader>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  className={cn(
                    GRID_LIST_ROW_CLASS,
                    onRowClick && "cursor-pointer",
                    rowClassName?.(row),
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  <GridCells columns={grid.visible} row={row} />
                  {trailingCell ? (
                    <TableCell
                      className={GRID_LIST_CELL_CLASS}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {trailingCell(row)}
                    </TableCell>
                  ) : null}
                  <GridRowEnd />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
